import { isLightingView, lightingColor } from './lightingVisual.js';
import { connectedStairs, stairFootprint } from './stairs.js';
import { siteSurfaces, surfaceColor, clippedGroundMarkings } from './siteAreas.js';
import { RAD, DEG, clamp, fmt, reqByKey } from './constants.js';
import { camGeom, camRanges, groundBand, cameraViews, rangeFor } from './optics.js';
import { polyBBox, polyCentroid } from './geometry.js';
import { sceneWalls } from './siteGeometry.js';
import { detailLevels } from './standards.js';
import { shellReferences, withReferenceBounds } from './building.js';
import { formatArea, formatLength } from './units.js';

/* The canvas is painted from the same CSS custom properties as the DOM, so it
   follows the theme instead of carrying a second, divergent palette. */
export function readPalette(root = document.documentElement) {
  const cs = getComputedStyle(root);
  const v = n => cs.getPropertyValue(n).trim();
  return {
    sheet: v('--cv-sheet'),
    grid: v('--cv-grid'),
    gridMaj: v('--cv-grid-major'),
    wall: v('--cv-wall'),
    glass: v('--cv-glass'),
    half: v('--cv-half'),
    solid: v('--cv-solid'),
    solidHalf: v('--cv-solid-half'),
    areaFill: v('--cv-area-fill'),
    areaLine: v('--cv-area-line'),
    exFill: v('--cv-exclude-fill'),
    exLine: v('--cv-exclude-line'),
    exHatch: v('--cv-exclude-hatch'),
    labelBg: v('--cv-label-bg'),
    zone: v('--cv-zone'),
    zoneFill: v('--cv-zone-fill'),
    zoneInk: v('--cv-zone-ink'),
    cam: v('--cv-cam'),
    lv: [v('--lv0'), v('--lv1'), v('--lv2'), v('--lv3'), v('--lv4'), '#6366db', '#9748c2', '#cc4591'],
    ramp: [1, 2, 3, 4, 5, 6].map(i => v('--ramp-' + i)),
    heatA: [
      parseFloat(v('--heat-blind-a')) || 0.3,
      parseFloat(v('--heat-a1')) || 0.42,
      parseFloat(v('--heat-a2')) || 0.52,
      parseFloat(v('--heat-a3')) || 0.62,
      parseFloat(v('--heat-a4')) || 0.72, 0.76, 0.8, 0.84
    ],
    accent: v('--accent'),
    accent2: v('--accent-ink'),
    text: v('--text'),
    text2: v('--text-2'),
    text3: v('--text-3'),
    good: v('--good'),
    warn: v('--warning'),
    crit: v('--critical')
  };
}

export function hexA(h, a) {
  const n = parseInt(String(h).slice(1), 16);
  if (!Number.isFinite(n)) return `rgba(128,128,128,${a})`;
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
}
function mixHex(a, b, t) {
  const A = parseInt(a.slice(1), 16), B = parseInt(b.slice(1), 16);
  const r = Math.round(((A >> 16) & 255) * (1 - t) + ((B >> 16) & 255) * t);
  const g = Math.round(((A >> 8) & 255) * (1 - t) + ((B >> 8) & 255) * t);
  const bl = Math.round((A & 255) * (1 - t) + (B & 255) * t);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + bl).toString(16).slice(1);
}

/* world <-> screen, given a view of { ox, oy, k } */
export const X = (view, wx) => (wx - view.ox) * view.k;
export const Y = (view, wy) => (wy - view.oy) * view.k;
export const WX = (view, sx) => sx / view.k + view.ox;
export const WY = (view, sy) => sy / view.k + view.oy;

/**
 * scene = {
 *   S, cov, view, w, h, PAL, viewMode, showHeat, showFov,
 *   sel, draft, hoverPt, bgImg, readOnly
 * }
 */
export function draw(ctx, scene) {
  const { S, view, w, h, PAL } = scene;
  ctx.save();
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = PAL.sheet;
  ctx.fillRect(0, 0, w, h);

  drawBg(ctx, scene);
  if (S.opt.showGrid) drawGrid(ctx, scene);
  drawAreas(ctx, scene);
  // Before any camera exists, every sample is blind; keep the plan neutral instead of red.
  const coverageShown = scene.showHeat && scene.cov && siteHasCameras(scene.S);
  if (scene.showHeat && isLightingView(scene.viewMode)) { if (scene.lighting) drawHeat(ctx, { ...scene, cov: scene.lighting }); }
  else if (coverageShown) drawHeat(ctx, scene);
  drawParking(ctx, scene);
  drawZones(ctx, scene);
  if (scene.showShellReference !== false) drawShellReference(ctx, scene);
  drawWalls(ctx, scene);
  drawAreaLabels(ctx, scene);
  if (coverageShown && !isLightingView(scene.viewMode)) drawBlind(ctx, scene);
  drawCams(ctx, scene);
  drawDraft(ctx, scene);
  drawScaleBar(ctx, scene);
  ctx.restore();
}

function drawAreaLabels(ctx,{S,view,PAL,labelMode,sel}) {
  if(labelMode==='selected' && sel?.type!=='area')return;
  ctx.save();ctx.font='500 10px sans-serif';ctx.textBaseline='top';ctx.textAlign='left';
  const occupied=[];
  const areas=S.areas.filter(a=>a.kind==='floor').map(a=>({a,b:polyBBox(a.pts)}));
  areas.sort((a,b)=>(a.b.x1-a.b.x0)*(a.b.y1-a.b.y0)-(b.b.x1-b.b.x0)*(b.b.y1-b.b.y0));
  for(const {a,b} of areas) {
    const on=sel?.type==='area'&&sel.id===a.id;
    if(labelMode==='selected'&&!on)continue;
    const width=(b.x1-b.x0)*view.k;
    if(!on&&(width<78||(b.y1-b.y0)*view.k<34))continue;
    const full=(a.name||'Room')+(a.registration!==undefined?' · provisional':'');let name=full;
    while(name.length>3&&ctx.measureText(name).width>width-16)name=name.slice(0,-1);
    if(name!==full)name=name.slice(0,-1)+'…';
    const x=X(view,b.x0)+7,y=Y(view,b.y0)+7;
    const box={x:x-3,y:y-2,w:ctx.measureText(name).width+6,h:15};
    if(!on&&labelMode!=='all'&&occupied.some(r=>box.x<r.x+r.w&&box.x+box.w>r.x&&box.y<r.y+r.h&&box.y+box.h>r.y))continue;
    occupied.push(box);
    ctx.fillStyle=hexA(PAL.sheet,.88);ctx.fillRect(box.x,box.y,box.w,box.h);
    ctx.fillStyle=PAL.text2;ctx.fillText(name,x,y);
  }
  ctx.restore();
}

function drawShellReference(ctx, { S, view, PAL }) {
  const references = shellReferences(S);
  if (!references.length) return;
  ctx.save(); ctx.lineWidth = 1.5; ctx.setLineDash([6, 5]);
  ctx.strokeStyle = hexA(PAL.accent, 0.65);
  for (const r of references) { pathPoly(ctx, view, r.pts); ctx.stroke(); }
  ctx.setLineDash([]); ctx.font = '500 11px sans-serif'; ctx.textAlign = 'left'; ctx.textBaseline = 'bottom';
  for (const r of references) {
    const p = [...r.pts].sort((a, b) => a.y - b.y || a.x - b.x)[0];
    const text = `${r.name} · ${r.floorName} reference`, x = X(view, p.x) + 5, y = Y(view, p.y) - 6;
    ctx.fillStyle = PAL.labelBg; ctx.fillRect(x - 3, y - 13, ctx.measureText(text).width + 6, 16);
    ctx.fillStyle = PAL.accent2 || PAL.accent; ctx.fillText(text, x, y);
  }
  ctx.restore();
}

function drawBg(ctx, { S, view, bgImg, sel, PAL }) {
  if (!S.bg || !bgImg || !bgImg.complete || !bgImg.naturalWidth) return;
  const mpp = S.bg.mpp || 0.02;
  const w = bgImg.naturalWidth * mpp * view.k;
  const h = bgImg.naturalHeight * mpp * view.k;
  ctx.save();
  ctx.globalAlpha = (S.bg.op ?? 55) / 100;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bgImg, X(view, S.bg.x), Y(view, S.bg.y), w, h);
  ctx.restore();
  if (sel?.type === 'bg') {
    ctx.strokeStyle = PAL.accent; ctx.lineWidth = 1.5; ctx.setLineDash([5, 4]);
    ctx.strokeRect(X(view, S.bg.x), Y(view, S.bg.y), w, h);
    ctx.setLineDash([]);
  }
}

function drawGrid(ctx, { S, view, w, h, PAL }) {
  let step = S.opt.grid;
  while (step * view.k < 7) step *= 5;
  const x0 = Math.floor(WX(view, 0) / step) * step, x1 = WX(view, w);
  const y0 = Math.floor(WY(view, 0) / step) * step, y1 = WY(view, h);
  ctx.lineWidth = 1;
  for (let x = x0; x <= x1; x += step) {
    const major = Math.abs(x / (step * 5) - Math.round(x / (step * 5))) < 1e-6;
    ctx.strokeStyle = major ? PAL.gridMaj : PAL.grid;
    ctx.beginPath();
    ctx.moveTo(Math.round(X(view, x)) + 0.5, 0);
    ctx.lineTo(Math.round(X(view, x)) + 0.5, h);
    ctx.stroke();
  }
  for (let y = y0; y <= y1; y += step) {
    const major = Math.abs(y / (step * 5) - Math.round(y / (step * 5))) < 1e-6;
    ctx.strokeStyle = major ? PAL.gridMaj : PAL.grid;
    ctx.beginPath();
    ctx.moveTo(0, Math.round(Y(view, y)) + 0.5);
    ctx.lineTo(w, Math.round(Y(view, y)) + 0.5);
    ctx.stroke();
  }
}

function pathPoly(ctx, view, pts) {
  ctx.beginPath();
  ctx.moveTo(X(view, pts[0].x), Y(view, pts[0].y));
  for (let i = 1; i < pts.length; i++) ctx.lineTo(X(view, pts[i].x), Y(view, pts[i].y));
  ctx.closePath();
}

function handles(ctx, view, PAL, pts) {
  ctx.fillStyle = PAL.accent; ctx.strokeStyle = PAL.sheet; ctx.lineWidth = 1.5;
  for (const p of pts) {
    ctx.beginPath();
    ctx.rect(X(view, p.x) - 3.5, Y(view, p.y) - 3.5, 7, 7);
    ctx.fill(); ctx.stroke();
  }
}

function drawAreas(ctx, scene) {
  const { S, view, PAL, sel } = scene;
  for (const { area, polygons } of siteSurfaces(S).layers) {
    ctx.beginPath();
    for (const rings of polygons) for (const ring of rings) { ring.forEach(([x, y], i) => i ? ctx.lineTo(X(view, x), Y(view, y)) : ctx.moveTo(X(view, x), Y(view, y))); ctx.closePath(); }
    ctx.fillStyle = surfaceColor(area, PAL.areaFill); ctx.fill('evenodd');
  }
  for (const a of S.areas) {
    if (a.pts.length < 3) continue;
    const on = sel?.type === 'area' && sel.id === a.id;
    pathPoly(ctx, view, a.pts);
    if (a.kind === 'floor') {
      // Rooms awaiting registration keep a dashed warning outline until placed by references.
      const provisional = a.registration !== undefined;
      ctx.strokeStyle = on ? PAL.accent : provisional ? PAL.warn : PAL.areaLine;
      ctx.lineWidth = on ? 2.2 : provisional ? 2 : 1.6;
      if (provisional) ctx.setLineDash([8, 5]);
      ctx.stroke();
      if (provisional) ctx.setLineDash([]);
    } else {
      ctx.fillStyle = PAL.exFill; ctx.fill();
      ctx.save(); ctx.clip();
      ctx.strokeStyle = PAL.exHatch; ctx.lineWidth = 1;
      const bb = polyBBox(a.pts);
      for (let d = -(bb.y1 - bb.y0); d < (bb.x1 - bb.x0) + (bb.y1 - bb.y0); d += 0.5) {
        ctx.beginPath();
        ctx.moveTo(X(view, bb.x0 + d), Y(view, bb.y0));
        ctx.lineTo(X(view, bb.x0 + d - (bb.y1 - bb.y0)), Y(view, bb.y1));
        ctx.stroke();
      }
      ctx.restore();
      ctx.strokeStyle = on ? PAL.accent : PAL.exLine;
      ctx.lineWidth = on ? 2 : 1.2;
      ctx.stroke();
    }
    if (on) handles(ctx, view, PAL, a.pts);
  }
}

function drawParking(ctx, { S, view }) {
  ctx.save(); ctx.strokeStyle = '#f7f2d6'; ctx.lineWidth = Math.max(1, 0.08 * view.k);
  for (const { area, polygons } of siteSurfaces(S).layers) {
    if (!['parking', 'road'].includes(area.purpose)) continue;
    ctx.beginPath();
    for (const [a, b] of clippedGroundMarkings(area, polygons)) { ctx.moveTo(X(view, a.x), Y(view, a.y)); ctx.lineTo(X(view, b.x), Y(view, b.y)); }
    ctx.stroke();
  }
  ctx.restore();
}

function heatColor(scene, i) {
  const { cov: R, PAL, viewMode, S } = scene;
  if (isLightingView(viewMode)) return lightingColor(R, i, viewMode);
  if (viewMode === 'grade') {
    const l = R.lvl[i];
    return hexA(PAL.lv[l], PAL.heatA[l]);
  }
  if (viewMode === 'req') {
    return (R.flags[i] & 4) ? hexA(PAL.lv[3], PAL.heatA[3]) : hexA(PAL.lv[0], PAL.heatA[0]);
  }
  if (viewMode === 'redund') {
    const n = R.nMeet[i];
    return n === 0 ? hexA(PAL.lv[0], PAL.heatA[0])
      : n === 1 ? hexA(PAL.lv[1], PAL.heatA[1])
        : n === 2 ? hexA(PAL.lv[3], PAL.heatA[3])
          : hexA(PAL.lv[4], PAL.heatA[4]);
  }
  const p = R.px[i], t = S.opt.t;
  if (p <= 0) return hexA(PAL.lv[0], PAL.heatA[0]);
  const f = clamp(Math.log(p / t.detect) / Math.log(t.identify * 1.6 / t.detect), 0, 1);
  const stops = PAL.ramp;
  const s = f * (stops.length - 1);
  const a = stops[Math.floor(s)], b = stops[Math.min(stops.length - 1, Math.floor(s) + 1)];
  return hexA(mixHex(a, b, s - Math.floor(s)), PAL.heatA[3]);
}

/** Whether any floor of the Site has a camera, so coverage is meaningful. */
export function siteHasCameras(S) { return (S?.cams?.length || 0) > 0 || (S?.floors || []).some(f => (f?.plan?.cams?.length || 0) > 0); }

function drawHeat(ctx, scene) {
  const { cov: R, view } = scene;
  const G = R.G, s = G.step, w = Math.ceil(s * view.k) + 1;
  if (w < 1) return;
  for (let i = 0; i < G.n; i++) {
    if (!G.inSite[i]) continue;
    ctx.fillStyle = heatColor(scene, i);
    ctx.fillRect(
      Math.round(X(view, G.sx[i] - s / 2)),
      Math.round(Y(view, G.sy[i] - s / 2)),
      w, w
    );
  }
}

function arrowHead(ctx, x, y, a, c) {
  ctx.fillStyle = c;
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x - Math.cos(a - 0.4) * 8, y - Math.sin(a - 0.4) * 8);
  ctx.lineTo(x - Math.cos(a + 0.4) * 8, y - Math.sin(a + 0.4) * 8);
  ctx.fill();
}

function drawZones(ctx, scene) {
  const { S, view, PAL, sel } = scene;
  for (const z of S.zones) {
    if (z.pts.length < 3) continue;
    const on = sel?.type === 'zone' && sel.id === z.id;
    pathPoly(ctx, view, z.pts);
    ctx.fillStyle = PAL.zoneFill; ctx.fill();
    ctx.strokeStyle = on ? PAL.accent : PAL.zone;
    ctx.lineWidth = on ? 2.2 : 1.4;
    ctx.setLineDash([7, 4]); ctx.stroke(); ctx.setLineDash([]);

    if (!on && (['selected', 'essential'].includes(scene.labelMode) || (scene.labelMode !== 'all' && view.k < 16))) continue;
    const c = polyCentroid(z.pts);
    ctx.font = '600 11px "Archivo", sans-serif';
    ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    const label = `${z.name} · ${reqByKey(z.req).k}`;
    const tw = ctx.measureText(label).width;
    ctx.fillStyle = PAL.labelBg;
    ctx.fillRect(X(view, c.x) - tw / 2 - 6, Y(view, c.y) - 9, tw + 12, 18);
    ctx.fillStyle = on ? PAL.accent2 : PAL.zoneInk;
    ctx.fillText(label, X(view, c.x), Y(view, c.y) + 0.5);

    if (z.req === 'plate') {
      const a = (z.axis || 0) * RAD, L = 1.6 * view.k;
      ctx.strokeStyle = PAL.zoneInk; ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(X(view, c.x) - Math.cos(a) * L, Y(view, c.y) - Math.sin(a) * L + 22);
      ctx.lineTo(X(view, c.x) + Math.cos(a) * L, Y(view, c.y) + Math.sin(a) * L + 22);
      ctx.stroke();
      arrowHead(ctx, X(view, c.x) + Math.cos(a) * L, Y(view, c.y) + Math.sin(a) * L + 22, a, PAL.zoneInk);
    }
    if (on) handles(ctx, view, PAL, z.pts);
  }
}

function drawWalls(ctx, scene) {
  const { S, view, PAL, sel } = scene;
  for (const w of sceneWalls(S)) {
    if (['stair', 'tree', 'plant'].includes(w.objectKind)) continue;
    if (w.roofPts || w.wallPts) continue;
    // The architectural cut plane omits high lintels, but furniture still needs
    // its complete top-down footprint when placed above that elevation.
    if ((w.bottom || 0) > 1.8 && !w.objectKind) continue;
    const on = sel?.type === 'wall' && sel.id === w.id;
    ctx.strokeStyle = on ? PAL.accent : w.color ? w.color
      : w.kind === 'glass' ? PAL.glass
        : w.kind === 'half' ? PAL.half : PAL.wall;
    ctx.lineWidth = w.objectKind && w.objectKind !== 'door' ? (w.thickness || 0.12) * view.k : on ? 5 : w.kind === 'solid' ? 3.4 : 2.4;
    ctx.lineCap = w.objectKind ? 'butt' : 'round';
    if (w.kind === 'glass') ctx.setLineDash([9, 5]);
    else if (w.kind === 'half') ctx.setLineDash([2, 3]);
    ctx.beginPath();
    ctx.moveTo(X(view, w.x1), Y(view, w.y1));
    ctx.lineTo(X(view, w.x2), Y(view, w.y2));
    ctx.stroke();
    ctx.setLineDash([]);
    if (on) handles(ctx, view, PAL, [{ x: w.x1, y: w.y1 }, { x: w.x2, y: w.y2 }]);
  }
}

/** Field-of-view geometry is only interesting where there is floor. */
function clipToSite(ctx, S, view) {
  const layers = siteSurfaces(S).layers;
  if (!layers.length) return false;
  ctx.beginPath();
  for (const { polygons } of layers) for (const rings of polygons) for (const ring of rings) {
    ring.forEach(([x, y], i) => i ? ctx.lineTo(X(view, x), Y(view, y)) : ctx.moveTo(X(view, x), Y(view, y)));
    ctx.closePath();
  }
  ctx.clip('evenodd');
  return true;
}

function drawCams(ctx, scene) {
  const { S, view, PAL, sel, showFov } = scene;
  for (const cam of S.cams) {
    const g = camGeom(S, cam);
    if (!g) continue;
    const on = sel?.type === 'cam' && sel.id === cam.id;
    const cx = X(view, cam.x), cy = Y(view, cam.y);

    if (showFov) for (const { cam: sensor, g } of cameraViews(S, cam)) {
      ctx.save();
      clipToSite(ctx, S, view);
      const levels = detailLevels(S).slice(1);
      const allRings = levels.map((level, i) => ({ r: Math.min(30, rangeFor(g, level.px)), c: PAL.lv[i + 1] }));
      const bF = groundBand(sensor, g, S.opt.hFace);
      const rings = on ? allRings : allRings.slice(-2);
      const a0 = (sensor.pan - g.hfov / 2) * RAD, a1 = (sensor.pan + g.hfov / 2) * RAD;
      for (const ring of rings) {
        const rg = Math.sqrt(Math.max(0.04, ring.r * ring.r - (cam.h - S.opt.hFace) ** 2));
        const near = Math.min(bF.near, rg);
        const far = Math.min(rg, Number.isFinite(bF.far) ? bF.far : rg);
        if (far <= near) continue;
        ctx.beginPath();
        if (g.hfov >= 359.5) {
          ctx.arc(cx, cy, far * view.k, 0, Math.PI * 2);
        } else {
          ctx.arc(cx, cy, near * view.k, a0, a1);
          ctx.arc(cx, cy, far * view.k, a1, a0, true);
          ctx.closePath();
        }
        ctx.strokeStyle = hexA(ring.c, on ? 0.8 : 0.34);
        ctx.lineWidth = on ? 1.3 : 1;
        ctx.stroke();
        if (on) { ctx.fillStyle = hexA(ring.c, 0.06); ctx.fill(); }
      }
      if (on && g.hfov < 359.5) {
        const edge = allRings[0].r * view.k;
        ctx.strokeStyle = hexA(PAL.accent, 0.8);
        ctx.setLineDash([4, 4]); ctx.lineWidth = 1;
        for (const a of [a0, a1]) {
          ctx.beginPath(); ctx.moveTo(cx, cy);
          ctx.lineTo(cx + Math.cos(a) * edge, cy + Math.sin(a) * edge);
          ctx.stroke();
        }
        ctx.setLineDash([]);
      }
      ctx.restore();
    }

    const a = cam.pan * RAD;
    ctx.save();
    ctx.translate(cx, cy); ctx.rotate(a);
    ctx.fillStyle = on ? PAL.accent : PAL.cam;
    ctx.strokeStyle = PAL.sheet; ctx.lineWidth = 2;
    if (g.hfov >= 359.5) {
      ctx.beginPath(); ctx.arc(0, 0, 8, 0, Math.PI * 2); ctx.stroke(); ctx.fill();
      ctx.fillStyle = PAL.sheet;
      ctx.beginPath(); ctx.arc(0, 0, 3, 0, Math.PI * 2); ctx.fill();
    } else {
      ctx.beginPath();
      ctx.moveTo(11, 0); ctx.lineTo(-5, -7); ctx.lineTo(-2, 0); ctx.lineTo(-5, 7);
      ctx.closePath(); ctx.stroke(); ctx.fill();
    }
    ctx.restore();

    if (on) {
      ctx.strokeStyle = PAL.accent; ctx.lineWidth = 1.2; ctx.setLineDash([3, 3]);
      ctx.beginPath(); ctx.arc(cx, cy, 18, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = PAL.accent2;
      ctx.beginPath();
      ctx.arc(cx + Math.cos(a) * 34, cy + Math.sin(a) * 34, 5, 0, Math.PI * 2);
      ctx.fill();
    }
    if (on || scene.labelMode === 'all' || (scene.labelMode !== 'selected' && view.k > 12)) {
      const label = !on && (scene.labelMode === 'essential' || (scene.labelMode !== 'all' && view.k < 25)) ? `C${S.cams.indexOf(cam) + 1}` : cam.name;
      ctx.font = '600 10px "IBM Plex Mono", monospace';
      ctx.textAlign = 'center'; ctx.textBaseline = 'top';
      ctx.fillStyle = PAL.labelBg;
      const tw = ctx.measureText(label).width;
      ctx.fillRect(cx - tw / 2 - 4, cy + 12, tw + 8, 14);
      ctx.fillStyle = on ? PAL.accent2 : PAL.text2;
      ctx.fillText(label, cx, cy + 14);
    }
  }
}

function drawBlind(ctx, scene) {
  const { cov, view, PAL, viewMode, S } = scene;
  if (viewMode !== 'grade') return;
  for (const b of cov.blind.filter(x => x.area >= 1.5).slice(0, 5)) {
    const bx = X(view, b.x), by = Y(view, b.y);
    ctx.strokeStyle = PAL.crit; ctx.lineWidth = 1.4;
    ctx.beginPath(); ctx.arc(bx, by, 11, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(bx - 4, by - 4); ctx.lineTo(bx + 4, by + 4);
    ctx.moveTo(bx + 4, by - 4); ctx.lineTo(bx - 4, by + 4);
    ctx.stroke();
    ctx.font = '600 10px "IBM Plex Mono", monospace';
    ctx.fillStyle = PAL.crit; ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.fillText(formatArea(b.area, S, 1), bx, by - 13);
  }
}

function drawDraft(ctx, scene) {
  const { draft, hoverPt, view, PAL, S } = scene;
  if (!draft) return;
  const pts = draft.pts.slice();
  if (hoverPt) pts.push(hoverPt);
  if (!pts.length) return;
  ctx.strokeStyle = PAL.accent; ctx.lineWidth = 1.8; ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(X(view, pts[0].x), Y(view, pts[0].y));
  for (let i = 1; i < pts.length; i++) ctx.lineTo(X(view, pts[i].x), Y(view, pts[i].y));
  if (draft.closed && pts.length > 2) ctx.closePath();
  ctx.stroke(); ctx.setLineDash([]);
  ctx.fillStyle = PAL.accent2;
  for (const p of draft.pts) {
    ctx.beginPath(); ctx.arc(X(view, p.x), Y(view, p.y), 3.5, 0, Math.PI * 2); ctx.fill();
  }
  if (pts.length >= 2) {
    const a = pts[pts.length - 2], b = pts[pts.length - 1];
    const d = Math.hypot(b.x - a.x, b.y - a.y);
    ctx.font = '600 11px "IBM Plex Mono", monospace';
    ctx.fillStyle = PAL.accent2; ctx.textAlign = 'left'; ctx.textBaseline = 'bottom';
    ctx.fillText(formatLength(d, S, 2), X(view, (a.x + b.x) / 2) + 8, Y(view, (a.y + b.y) / 2) - 6);
  }
}

function drawScaleBar(ctx, { view, h, PAL, S }) {
  const target = 110;
  let m = target / view.k;
  const pow = 10 ** Math.floor(Math.log10(m));
  m = [1, 2, 5, 10].map(v => v * pow)
    .reduce((a, b) => (Math.abs(b - m) < Math.abs(a - m) ? b : a));
  const px = m * view.k;
  const x = 16, y = h - 52;
  ctx.strokeStyle = PAL.text2; ctx.lineWidth = 1.4;
  ctx.beginPath();
  ctx.moveTo(x, y - 5); ctx.lineTo(x, y); ctx.lineTo(x + px, y); ctx.lineTo(x + px, y - 5);
  ctx.stroke();
  ctx.font = '500 10.5px "IBM Plex Mono", monospace';
  ctx.fillStyle = PAL.text2; ctx.textAlign = 'left'; ctx.textBaseline = 'bottom';
  ctx.fillText(formatLength(m, S, m < 1 ? 2 : 1), x, y - 8);
}

/* ---- viewport helpers --------------------------------------------------- */

export function fitView(S, w, h, showShellReference = true) {
  const fl = S.areas.filter(a => a.kind === 'floor');
  let b = null;
  if (fl.length) {
    b = polyBBox(fl.flatMap(a => a.pts));
  } else if (S.cams.length) {
    b = polyBBox(S.cams.map(c => ({ x: c.x, y: c.y })));
    b.x0 -= 5; b.y0 -= 5; b.x1 += 5; b.y1 += 5;
  }
  b = withReferenceBounds(b, connectedStairs(S).map(s => ({ pts: stairFootprint(s) })));
  if (showShellReference) b = withReferenceBounds(b, shellReferences(S));
  if (!b) return { ox: -2, oy: -2, k: 30 };
  const pad = 60;
  const k = clamp(Math.min(
    (w - pad * 2) / Math.max(0.5, b.x1 - b.x0),
    (h - pad * 2) / Math.max(0.5, b.y1 - b.y0)
  ), 1, 400);
  return {
    k,
    ox: (b.x0 + b.x1) / 2 - w / (2 * k),
    oy: (b.y0 + b.y1) / 2 - h / (2 * k)
  };
}
